import { useEffect } from "react";

const DEFAULT_MESSAGE = "Changes you made may not be saved.";

function isInternalAppHref(href: string): boolean {
  if (href.startsWith("/")) {
    return true;
  }
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Browser leave guard for dirty forms: native beforeunload on tab close/refresh,
 * and confirm on in-app anchor navigation (BrowserRouter has no useBlocker).
 */
export function useBrowserUnsavedChangesGuard(active: boolean, message = DEFAULT_MESSAGE): void {
  useEffect(() => {
    if (!active) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [active, message]);

  useEffect(() => {
    if (!active) {
      return;
    }
    const onDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      if (event.button !== 0) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) {
        return;
      }
      const href = anchor.getAttribute("href");
      if (href == null || href.trim() === "" || href.startsWith("#")) {
        return;
      }
      if (!isInternalAppHref(href)) {
        return;
      }
      const nextUrl = new URL(href, window.location.origin);
      const currentUrl = new URL(window.location.href);
      if (
        nextUrl.pathname === currentUrl.pathname &&
        nextUrl.search === currentUrl.search &&
        nextUrl.hash === currentUrl.hash
      ) {
        return;
      }
      const confirmed = window.confirm(message);
      if (!confirmed) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("click", onDocumentClick, true);
    return () => document.removeEventListener("click", onDocumentClick, true);
  }, [active, message]);
}
